import React, { useContext } from "react";
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate,
  useLocation,
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext
} from "react-router-dom";
import Login from "./pages/Login.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import Register from "./pages/Register.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import ClubConfig from "./pages/ClubConfig.jsx";
import ClubAdmin from "./pages/ClubAdmin.jsx";
import ClubContacts from "./pages/ClubContacts.jsx";
import Memberships from "./pages/Memberships.jsx";
import AuthProvider, { AuthContext } from "./context/AuthContext.jsx";
import { ClubProvider } from "./context/ClubContext";
import Layout from "./components/Layout";
import VerifyUser from "./pages/VerifyUser";
import ChangePassword from "./pages/ChangePassword";
import Analytics from "./pages/Analytics";

// Enable future flags
const router = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

// ProtectedRoute component - redirects to login if not authenticated
const ProtectedRoute = ({ element }) => {
  const { user, isCheckingAuth } = useContext(AuthContext);
  const location = useLocation();
  
  console.log('ProtectedRoute render - user:', !!user, 'isCheckingAuth:', isCheckingAuth);
  
  // If we have a user (even if checking auth), allow mounting so API calls can start in parallel
  // This reduces the gap between refreshToken and other API calls
  if (user) {
    console.log('ProtectedRoute: User exists, mounting component');
    return <Layout>{element}</Layout>;
  }
  
  // Show nothing while checking auth to prevent flash of wrong content
  if (isCheckingAuth) {
    console.log('ProtectedRoute: Checking auth, showing nothing');
    return null;
  }
  
  console.log('ProtectedRoute: No user, redirecting to login');
  const redirectPath = `${location.pathname}${location.search}`;
  localStorage.setItem('postLoginRedirect', redirectPath);
  return <Navigate to="/login" />;
};

// PublicRoute component - redirects to dashboard if already authenticated
const PublicRoute = ({ element }) => {
  const { user, isCheckingAuth } = useContext(AuthContext);
  
  // Show nothing while checking auth to prevent flash of wrong content
  if (isCheckingAuth) {
    return null;
  }
  
  if (user) {
    const redirect = localStorage.getItem('postLoginRedirect');
    if (redirect && redirect.startsWith('/')) {
      localStorage.removeItem('postLoginRedirect');
      return <Navigate to={redirect} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return element;
};

// RootRoute component - redirects based on authentication status
const RootRoute = () => {
  const { user, isCheckingAuth } = useContext(AuthContext);
  
  // Show nothing while checking auth to prevent flash of wrong content
  if (isCheckingAuth) {
    return null;
  }
  
  return user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />;
};

// Loading component for auth check
const AuthLoadingIndicator = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-center">
        <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="mt-4 text-slate-600">Loading...</p>
      </div>
    </div>
  );
};

// AppRoutes component to handle auth checking
const AppRoutes = () => {
  const { isCheckingAuth, user } = useContext(AuthContext);
  
  console.log('AppRoutes render - isCheckingAuth:', isCheckingAuth, 'user:', !!user);
  
  // Only show loading indicator if we're checking auth AND don't have a user yet
  // If we have a user (even if checking auth), allow routes to render so API calls can start
  if (isCheckingAuth && !user) {
    console.log('AppRoutes: Showing loading indicator');
    return <AuthLoadingIndicator />;
  }
  
  console.log('AppRoutes: Rendering routes');
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<PublicRoute element={<Login />} />} />
      <Route path="/reset-password" element={<PublicRoute element={<ResetPassword />} />} />
      <Route path="/register" element={<PublicRoute element={<Register />} />} />
      <Route path="/verify" element={<PublicRoute element={<VerifyUser />} />} />
      <Route path="/resetPassword" element={<PublicRoute element={<ChangePassword />} />} />
      <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
      <Route path="/club-config" element={<ProtectedRoute element={<ClubConfig />} />} />
      <Route path="/club-admin" element={<ProtectedRoute element={<ClubAdmin />} />} />
      <Route path="/club-contacts" element={<ProtectedRoute element={<ClubContacts />} />} />
      <Route path="/memberships" element={<ProtectedRoute element={<Memberships />} />} />
      <Route path="/analytics" element={<ProtectedRoute element={<Analytics />} />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <ClubProvider>
        <UNSAFE_DataRouterContext.Provider value={router}>
          <UNSAFE_DataRouterStateContext.Provider value={router}>
            <Router>
              <AppRoutes />
            </Router>
          </UNSAFE_DataRouterStateContext.Provider>
        </UNSAFE_DataRouterContext.Provider>
      </ClubProvider>
    </AuthProvider>
  );
}

export default App;
